import { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronRight, Check, X, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { AdjustmentTable, AdjustmentItem } from '@/data/mockAdjustments';

const ACCOUNT_OPTIONS = [
  'Revenue',
  'Cost of Sales',
  'Government Grants',
  'Operating Expenses',
  'Employee Costs',
  'Depreciation & Amortisation',
  'Interest & Finance Costs',
  'Trade Receivables',
  'Trade Payables',
  'Inventory',
  'Borrowings',
  'Cash & Equivalents',
  'Other Income',
  'Other Expenses',
];

const SUB_ACCOUNT_OPTIONS: Record<string, string[]> = {
  'Revenue': ['Product Sales', 'Service Revenue', 'Subscription Revenue', 'Licensing Revenue', 'Other Revenue'],
  'Cost of Sales': ['Direct Materials', 'Direct Labour', 'Manufacturing Overhead', 'Freight & Distribution'],
  'Government Grants': ['One-off Subsidies', 'Recurring Grants', 'R&D Grants', 'Export Incentives'],
  'Operating Expenses': ['Rent & Occupancy', 'Marketing & Advertising', 'Travel & Entertainment', 'Professional Fees', 'IT & Software'],
  'Employee Costs': ['Salaries & Wages', 'Bonuses', 'Superannuation', 'Share-based Payments', 'Redundancy Costs'],
  'Depreciation & Amortisation': ['PP&E Depreciation', 'ROU Asset Depreciation', 'Intangible Amortisation', 'Goodwill Impairment'],
  'Interest & Finance Costs': ['Bank Interest', 'Lease Interest', 'Facility Fees', 'Foreign Exchange'],
  'Trade Receivables': ['Current', 'Overdue 0-30 days', 'Overdue 30-60 days', 'Overdue 60+ days'],
  'Trade Payables': ['Current', 'Overdue', 'Accrued Expenses', 'Related Party Payables'],
  'Inventory': ['Raw Materials', 'Work in Progress', 'Finished Goods', 'Obsolete Stock'],
  'Borrowings': ['Term Loans', 'Revolving Credit', 'Shareholder Loans', 'Hire Purchase'],
  'Cash & Equivalents': ['Operating Accounts', 'Term Deposits', 'Restricted Cash'],
  'Other Income': ['Government Grants', 'Gain on Disposal', 'Insurance Recoveries', 'Sundry Income'],
  'Other Expenses': ['Restructuring Costs', 'Legal Settlements', 'Write-offs', 'Donations'],
};

interface AdjustmentTableCardProps {
  table: AdjustmentTable;
  onAccept: (tableId: string, itemId: string) => void;
  onReject: (tableId: string, itemId: string) => void;
  onValueChange?: (tableId: string, itemId: string, period: string, value: number | null, monthKey?: string) => void;
  onAddAdjustment?: () => void;
  onPasteFromExcel?: () => void;
}

function EditableCell({ value, onSave, disabled }: {
  value: number | string | null;
  onSave: (newValue: number | null) => void;
  disabled?: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    setIsEditing(false);
    const trimmed = editValue.trim();
    if (trimmed === '' || trimmed === '-') {
      onSave(null);
    } else {
      const num = Number(trimmed.replace(/[(),]/g, ''));
      if (!isNaN(num)) {
        onSave(trimmed.startsWith('(') || trimmed.startsWith('-') ? -Math.abs(num) : num);
      }
    }
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSave();
          if (e.key === 'Escape') setIsEditing(false);
        }}
        className="w-full min-w-[60px] bg-background border border-ring rounded px-1.5 py-0.5 text-right font-mono text-xs outline-none focus:ring-2 focus:ring-ring/20"
      />
    );
  }

  return (
    <span
      onDoubleClick={() => {
        if (disabled) return;
        setIsEditing(true);
        setEditValue(value === null || value === undefined ? '' : String(value));
      }}
      className={cn('block cursor-text hover:bg-accent/30 rounded px-1 -mx-1 transition-colors', disabled && 'cursor-default')}
      title={disabled ? undefined : 'Double-click to edit'}
    >
      {formatValue(value)}
    </span>
  );
}

function EditableLabelCell({ value, onSave }: { value: string; onSave: (v: string) => void }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    setIsEditing(false);
    const trimmed = editValue.trim();
    if (trimmed) onSave(trimmed);
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSave();
          if (e.key === 'Escape') setIsEditing(false);
        }}
        className="w-full min-w-[120px] bg-background border border-ring rounded px-1.5 py-0.5 text-sm outline-none focus:ring-2 focus:ring-ring/20"
      />
    );
  }

  return (
    <span
      onDoubleClick={() => { setIsEditing(true); setEditValue(value); }}
      className="block cursor-text hover:bg-accent/30 rounded px-1 -mx-1 transition-colors"
      title="Double-click to rename"
    >
      {value}
    </span>
  );
}

const formatValue = (value: number | string | null): string => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'string') return value;
  if (value < 0) return `(${Math.abs(value).toLocaleString()})`;
  return value.toLocaleString();
};

const getStatusBadge = (status: AdjustmentItem['status']) => {
  switch (status) {
    case 'accepted':
      return <span className="inline-flex items-center text-[11px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">Accepted</span>;
    case 'rejected':
      return <span className="inline-flex items-center text-[11px] font-medium text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full">Rejected</span>;
    default:
      return <span className="inline-flex items-center text-[11px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">Pending</span>;
  }
};

export function AdjustmentTableCard({ table, onAccept, onReject, onValueChange, onAddAdjustment, onPasteFromExcel }: AdjustmentTableCardProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [expandedPeriods, setExpandedPeriods] = useState<Set<string>>(new Set());
  const [showMappingCols, setShowMappingCols] = useState(false);

  const togglePeriodExpand = (period: string) => {
    setExpandedPeriods(prev => {
      const next = new Set(prev);
      if (next.has(period)) next.delete(period);
      else next.add(period);
      return next;
    });
  };

  const pendingCount = table.items.filter(item => item.status === 'pending' && !item.isSectionHeader && !item.isSubtotal).length;
  const acceptedCount = table.items.filter(item => item.status === 'accepted').length;
  const totalAdjustable = table.items.filter(item => !item.isSectionHeader && !item.isSubtotal).length;

  // Build the column structure: for each period, either one column or expanded monthly columns
  const getColumnsForPeriod = (period: string) => {
    if (!expandedPeriods.has(period)) return [period]; // collapsed: single FY column
    // expanded: get monthly labels from the first item that has monthlyBreakdown for this period
    const itemWithMonthly = table.items.find(item => item.monthlyBreakdown?.[period]);
    if (!itemWithMonthly) return [period];
    return itemWithMonthly.monthlyBreakdown![period].map(m => m.month);
  };

  const getMonthlyValue = (item: AdjustmentItem, period: string, monthKey: string): number | string | null => {
    const breakdown = item.monthlyBreakdown?.[period];
    if (!breakdown) return null;
    const entry = breakdown.find(m => m.month === monthKey);
    return entry?.value ?? null;
  };

  return (
    <Card variant="premium">
      <CardHeader
        className="cursor-pointer select-none hover:bg-muted/30 transition-colors rounded-t-lg"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-foreground/50" />
            ) : (
              <ChevronRight className="h-4 w-4 text-foreground/50" />
            )}
            <CardTitle className="text-base font-semibold">{table.title}</CardTitle>
          </div>
          <div className="flex items-center gap-3">
            {acceptedCount > 0 && (
              <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                {acceptedCount} accepted
              </span>
            )}
            {pendingCount > 0 && (
              <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                {pendingCount} pending
              </span>
            )}
            <span className="text-xs text-muted-foreground">
              {acceptedCount + (table.items.filter(i => i.status === 'rejected').length)}/{totalAdjustable}
            </span>
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="pt-0">
          <div className="overflow-x-auto rounded-lg border border-border/60">
            <table className="w-full text-sm">
              <thead>
                {/* Top header row */}
                <tr className="bg-[#f8f6f3]">
                  <th className="px-4 py-2.5 text-left font-medium text-foreground/60 text-xs uppercase tracking-wider border-b border-border/40" style={{ width: '35%', minWidth: 280 }} rowSpan={2}>
                    {table.unit}
                  </th>
                  <th className="px-2 py-2.5 text-center font-medium text-foreground/60 text-xs uppercase tracking-wider w-10 border-b border-border/40" rowSpan={2}>
                    Ref
                  </th>
                  {/* Collapsible Mapping group */}
                  <th
                    colSpan={showMappingCols ? 2 : 1}
                    className="px-2 py-2.5 text-center font-medium text-foreground/60 text-xs uppercase tracking-wider border-b border-border/40 border-l border-border/30"
                  >
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowMappingCols(!showMappingCols); }}
                      className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                    >
                      {showMappingCols
                        ? <ChevronDown className="w-3.5 h-3.5" />
                        : <ChevronRight className="w-3.5 h-3.5" />}
                      <span className="font-mono text-xs">Mapping</span>
                    </button>
                  </th>
                  {table.periods.map((period) => {
                    const cols = getColumnsForPeriod(period);
                    const isOpen = expandedPeriods.has(period);
                    return (
                      <th
                        key={period}
                        colSpan={cols.length}
                        className="px-2 py-2.5 text-center font-medium text-foreground/60 text-xs uppercase tracking-wider border-b border-border/40 border-l border-border/30"
                      >
                        <button
                          onClick={(e) => { e.stopPropagation(); togglePeriodExpand(period); }}
                          className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                        >
                          {isOpen
                            ? <ChevronDown className="w-3.5 h-3.5" />
                            : <ChevronRight className="w-3.5 h-3.5" />}
                          <span className="font-mono">{period}</span>
                        </button>
                      </th>
                    );
                  })}
                  <th className="px-4 py-2.5 text-center font-medium text-foreground/60 text-xs uppercase tracking-wider w-28 border-b border-border/40" rowSpan={2}>
                    Status
                  </th>
                  <th className="px-4 py-2.5 text-center font-medium text-foreground/60 text-xs uppercase tracking-wider w-24 border-b border-border/40" rowSpan={2}>
                    Actions
                  </th>
                </tr>
                {/* Sub-header row */}
                <tr className="bg-[#f8f6f3]/60">
                  {/* Mapping sub-headers */}
                  {showMappingCols ? (
                    <>
                      <th className="px-2 py-1.5 text-left text-[10px] text-muted-foreground/80 font-normal border-l border-border/30">Account</th>
                      <th className="px-2 py-1.5 text-left text-[10px] text-muted-foreground/80 font-normal">Sub-account</th>
                    </>
                  ) : (
                    <th className="border-l border-border/30" />
                  )}
                  {table.periods.map((period) => {
                    const cols = getColumnsForPeriod(period);
                    const isOpen = expandedPeriods.has(period);
                    if (!isOpen) {
                      return <th key={period} className="border-l border-border/30" />;
                    }
                    return cols.map((month, i) => (
                      <th
                        key={`${period}-${month}`}
                        className={cn(
                          'px-2 py-1.5 text-right font-mono text-[10px] text-muted-foreground/80 font-normal',
                          i === 0 && 'border-l border-border/30'
                        )}
                      >
                        {month}
                      </th>
                    ));
                  })}
                </tr>
              </thead>
              <tbody>
                {table.items.map((item, index) => (
                  <tr
                    key={item.id}
                    className={cn(
                      'border-t border-border/40 transition-colors',
                      index % 2 === 1 && 'bg-muted/10',
                      item.isSubtotal && 'bg-muted/30 font-semibold border-t-border',
                      item.isSectionHeader && 'bg-muted/15',
                      item.status === 'rejected' && 'opacity-40',
                      item.status === 'accepted' && 'bg-emerald-500/[0.03]',
                      !item.isSectionHeader && !item.isSubtotal && 'hover:bg-muted/20'
                    )}
                  >
                    <td className={cn(
                      'px-4 py-2.5',
                      item.isItalic && 'italic text-muted-foreground',
                      item.isSubtotal && 'font-semibold'
                    )}>
                      <div className="flex items-center gap-1.5">
                        {item.id.includes('-manual-') ? (
                          <EditableLabelCell
                            value={item.label}
                            onSave={(newLabel) => onValueChange?.(table.id, item.id, '__label__', null, newLabel)}
                          />
                        ) : item.label}
                        {item.isNew && (
                          <span className="text-[9px] font-bold text-purple-600 bg-purple-100 px-1.5 py-px rounded-full">NEW</span>
                        )}
                        {item.reason && (
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-100 text-amber-600 cursor-help shrink-0">
                                  <Info className="w-2.5 h-2.5" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-xs text-xs leading-relaxed p-3 bg-white text-foreground border border-border shadow-lg">
                                <p className="font-semibold mb-1">Why this adjustment?</p>
                                <p>{item.reason}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-2.5 text-center">
                      {item.refNumber && (
                        <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-medium bg-muted text-muted-foreground rounded">
                          {item.refNumber}
                        </span>
                      )}
                    </td>
                    {/* Mapping columns */}
                    {showMappingCols ? (
                      <>
                        <td className="px-2 py-2.5 text-xs text-muted-foreground border-l border-border/30 whitespace-nowrap">
                          <Select
                            value={item.account || ''}
                            onValueChange={(val) => onValueChange?.(table.id, item.id, '__account__', null, val)}
                          >
                            <SelectTrigger className="h-7 text-xs border-none bg-transparent shadow-none px-1 w-[140px]">
                              <SelectValue placeholder="Select account" />
                            </SelectTrigger>
                            <SelectContent>
                              {ACCOUNT_OPTIONS.map((opt) => (
                                <SelectItem key={opt} value={opt} className="text-xs">{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
                          <Select
                            value={item.subAccount || ''}
                            onValueChange={(val) => onValueChange?.(table.id, item.id, '__subaccount__', null, val)}
                          >
                            <SelectTrigger className="h-7 text-xs border-none bg-transparent shadow-none px-1 w-[160px]">
                              <SelectValue placeholder="Select sub-account" />
                            </SelectTrigger>
                            <SelectContent>
                              {(SUB_ACCOUNT_OPTIONS[item.account || ''] || []).map((opt) => (
                                <SelectItem key={opt} value={opt} className="text-xs">{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                      </>
                    ) : (
                      <td className="px-2 py-2.5 text-xs text-muted-foreground border-l border-border/30 whitespace-nowrap max-w-[80px] truncate" title={item.account || ''}>
                        {item.account || ''}
                      </td>
                    )}
                    {table.periods.map((period) => {
                      const isOpen = expandedPeriods.has(period);
                      if (!isOpen) {
                        const isEditable = !item.isSectionHeader && !item.isSubtotal;
                        return (
                          <td
                            key={period}
                            className={cn(
                              'px-4 py-2.5 text-right font-mono border-l border-border/30',
                              item.isSubtotal && 'font-semibold',
                            )}
                          >
                            {item.isSectionHeader ? '' : isEditable ? (
                              <EditableCell
                                value={item.values[period]}
                                onSave={(v) => onValueChange?.(table.id, item.id, period, v)}
                                disabled={!onValueChange}
                              />
                            ) : formatValue(item.values[period])}
                          </td>
                        );
                      }
                      const cols = getColumnsForPeriod(period);
                      const isEditable = !item.isSectionHeader && !item.isSubtotal;
                      return cols.map((month, i) => (
                        <td
                          key={`${period}-${month}`}
                          className={cn(
                            'px-2 py-2.5 text-right font-mono text-xs',
                            i === 0 && 'border-l border-border/30',
                            item.isSubtotal && 'font-semibold',
                          )}
                        >
                          {item.isSectionHeader ? '' : isEditable ? (
                            <EditableCell
                              value={getMonthlyValue(item, period, month)}
                              onSave={(v) => onValueChange?.(table.id, item.id, period, v, month)}
                              disabled={!onValueChange}
                            />
                          ) : formatValue(getMonthlyValue(item, period, month))}
                        </td>
                      ));
                    })}
                    <td className="px-4 py-2.5 text-center">
                      {!item.isSectionHeader && !item.isSubtotal && getStatusBadge(item.status)}
                    </td>
                    <td className="px-4 py-2.5">
                      {!item.isSectionHeader && !item.isSubtotal && (
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              onAccept(table.id, item.id);
                            }}
                            disabled={item.status === 'accepted'}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-destructive hover:bg-destructive/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              onReject(table.id, item.id);
                            }}
                            disabled={item.status === 'rejected'}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {(onAddAdjustment || onPasteFromExcel) && (
                  <tr className="border-t border-dashed border-border/40">
                    <td colSpan={99} className="px-4 py-2.5">
                      <div className="flex items-center gap-3">
                        {onAddAdjustment && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onAddAdjustment(); }}
                            className="flex items-center gap-2 text-sm text-primary/60 hover:text-primary transition-colors group"
                          >
                            <span className="w-5 h-5 rounded-full border border-primary/30 group-hover:border-primary/60 group-hover:bg-primary/10 flex items-center justify-center transition-all">
                              <span className="text-xs font-medium leading-none">+</span>
                            </span>
                            <span className="font-medium">Add adjustment</span>
                          </button>
                        )}
                        {onPasteFromExcel && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onPasteFromExcel(); }}
                            className="flex items-center gap-1.5 text-sm text-muted-foreground/60 hover:text-foreground transition-colors"
                          >
                            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                            <span className="font-medium">Paste from Excel</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
